/**
 * MobileActionSheet — bottom sheet shown when the user taps the centre
 * FAB on mobile. Three options at v1:
 *   1. Scan receipt → opens camera via ScanReceiptContext
 *   2. Add transaction → deep-link /transactions?new=1
 *   3. Import statement → deep-link /import
 *
 * Why a Sheet and not a Popover? Sheet's bottom-anchored layout puts
 * the actions where the thumb naturally rests on mobile (a thumb-zone
 * principle Apple HIG and Material 3 both endorse). Popovers float
 * above the FAB and force the user to reach up the screen.
 */
import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Camera, Plus, Upload, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useScanReceipt } from '@/context/ScanReceiptContext';

function Row({ icon: Icon, title, hint, onClick, testid, primary }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'w-full flex items-center gap-3 rounded-2xl px-3 py-3 text-left transition-colors ' +
        'border border-border/60 bg-background hover:bg-muted active:bg-muted/80 ' +
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ' +
        (primary ? 'border-primary/40 bg-primary/[0.05]' : '')
      }
      data-testid={testid}
    >
      <div className={'h-10 w-10 rounded-xl grid place-items-center shrink-0 ' + (primary ? 'bg-primary/15 text-primary' : 'bg-muted text-foreground/80')}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-[11px] text-muted-foreground truncate">{hint}</div>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </button>
  );
}

export default function MobileActionSheet({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { openScanner } = useScanReceipt();

  const close = () => onOpenChange?.(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-3xl border-t border-border/70 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4"
        data-testid="mobile-action-sheet"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="text-base">Log something quickly</SheetTitle>
          <SheetDescription className="text-xs">
            Capture a receipt, add a transaction, or import a statement.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          <Row
            icon={Camera}
            title="Scan receipt"
            hint="Take a photo — we'll fill in amount, merchant and date"
            primary
            onClick={() => { close(); openScanner(); }}
            testid="mobile-action-scan-receipt"
          />
          <Row
            icon={Plus}
            title="Add transaction"
            hint="Manually log income, expense, or transfer"
            onClick={() => { close(); navigate('/transactions?new=1'); }}
            testid="mobile-action-add-transaction"
          />
          <Row
            icon={Upload}
            title="Import statement"
            hint="Upload a bank PDF — we'll parse the transactions"
            onClick={() => { close(); navigate('/import'); }}
            testid="mobile-action-import-statement"
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
